import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../utils/api';
import { formatCurrency, formatPercent, getMarginStatus } from '../../utils/formatters';
import Avatar from '../../components/Avatar';
import BulkGigModal from '../../components/BulkGigModal';
import ExportScheduleModal from '../../components/ExportScheduleModal';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  MoreVertical,
  MoreHorizontal,
  X,
  Calendar,
  Zap,
  List,
  Download
} from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, addWeeks, subWeeks, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, isToday, isSameDay } from 'date-fns';

// Helper to format 24h time to 12h
function formatTime12h(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

// Helper to compare dates ignoring timezone issues
// Extracts YYYY-MM-DD from ISO string and compares with local date
function isSameDateString(isoDateString, localDate) {
  const dateFromApi = isoDateString.split('T')[0]; // "2026-01-10"
  const localDateStr = format(localDate, 'yyyy-MM-dd'); // "2026-01-10"
  return dateFromApi === localDateStr;
}

// Hook to detect mobile screen
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
}

// Mobile Week Strip Component
function WeekStrip({ days, selectedDate, onSelectDate, data }) {
  const hasGigs = (date) => {
    if (!data) return false;
    const dateKey = format(date, 'yyyy-MM-dd');
    return data.dailySummaries[dateKey]?.gigCount > 0;
  };

  return (
    <div className="bg-white border-b border-gray-200 px-2 py-3">
      <div className="flex justify-between items-center">
        {days.map((day) => {
          const isSelected = isSameDay(day, selectedDate);
          const isCurrentDay = isToday(day);
          const hasGigsToday = hasGigs(day);
          
          return (
            <button
              key={day.toISOString()}
              onClick={() => onSelectDate(day)}
              className="flex flex-col items-center flex-1"
            >
              <span className={`text-xs font-medium ${isCurrentDay ? 'text-hub-600' : 'text-gray-500'}`}>
                {format(day, 'EEE')}
              </span>
              <span 
                className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold mt-1
                  ${isSelected ? 'bg-hub-600 text-white' : isCurrentDay ? 'text-hub-600' : 'text-gray-900'}
                `}
              >
                {format(day, 'd')}
              </span>
              {/* Dot indicator for days with gigs */}
              <span 
                className={`w-1.5 h-1.5 rounded-full mt-1 ${hasGigsToday ? 'bg-hub-500' : 'bg-transparent'}`}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Mobile Daily Totals Component
function MobileDailyTotals({ summary, expanded, onToggle }) {
  if (!summary) {
    return (
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
        <div className="text-sm text-gray-500">No gigs scheduled</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border-b border-gray-200">
      <button 
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Daily Totals</span>
          <span className="text-xs bg-hub-100 text-hub-700 px-2 py-0.5 rounded-full">
            {summary.gigCount} gig{summary.gigCount !== 1 ? 's' : ''}
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      
      {expanded && (
        <div className="px-4 pb-3 grid grid-cols-2 gap-3">
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="text-xs text-gray-500">Total Gigs</div>
            <div className="text-lg font-semibold">{summary.gigCount}</div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="text-xs text-gray-500">Contractors</div>
            <div className="text-lg font-semibold">{summary.assignedContractorCount || 0}</div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="text-xs text-gray-500">Billed</div>
            <div className="text-lg font-semibold">{formatCurrency(summary.billed)}</div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="text-xs text-gray-500">Payouts</div>
            <div className="text-lg font-semibold">{formatCurrency(summary.payout)}</div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-gray-200 col-span-2">
            <div className="text-xs text-gray-500">Margin</div>
            <div className={`text-lg font-bold margin-${getMarginStatus(summary.margin)}`}>
              {formatCurrency(summary.productionFee)} ({formatPercent(summary.margin)})
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile Gig Card Component
function MobileGigCard({ assignment, onClick }) {
  const isDraft = assignment.gigStatus === 'DRAFT';
  const bgColor = isDraft ? 'white' : (assignment.clientColor || '#FEF3C7'); // Default yellow if no color
  
  return (
    <div 
      onClick={onClick}
      className={`rounded-lg p-3 mb-2 cursor-pointer shadow-sm ${isDraft ? 'border-2 border-yellow-500 bg-white' : ''}`}
      style={isDraft ? {} : { backgroundColor: bgColor }}
    >
      {isDraft && (
        <div className="text-xs font-bold text-yellow-600 mb-1">● DRAFT</div>
      )}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900">{assignment.location}</div>
          <div className="text-sm text-gray-700 flex items-center gap-1">
            <span>📍</span>
            <span>{assignment.client}</span>
          </div>
          <div className="text-sm text-gray-600">
            ↳ {assignment.venue}/{assignment.location}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {formatTime12h(assignment.startTime)} - {formatTime12h(assignment.endTime)}
          </div>
          {assignment.role && (
            <div className="text-xs text-gray-500 mt-1">{assignment.role}</div>
          )}
          {/* Financial info for admin */}
          <div className="mt-2 pt-2 border-t border-gray-300/50 text-xs space-y-0.5">
            <div className="flex justify-between">
              <span className="text-gray-600">Billed:</span>
              <span className="font-medium">{formatCurrency(assignment.clientBilled)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Payout:</span>
              <span className="font-medium">{formatCurrency(assignment.payout)}</span>
            </div>
          </div>
        </div>
        
        {/* Contractor avatar */}
        <div className="ml-3 flex flex-col items-end">
          <div 
            className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center text-sm font-semibold text-gray-700 shadow-sm"
            title={assignment.contractorName}
          >
            {assignment.contractorName?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
          </div>
        </div>
      </div>
    </div>
  );
}

// Mobile View Component
function MobileSchedulerView({ data, selectedDate, setSelectedDate, weekDays, navigateToGig, navigateToNewGig }) {
  const [totalsExpanded, setTotalsExpanded] = useState(false);
  
  const dateKey = format(selectedDate, 'yyyy-MM-dd');
  const dailySummary = data?.dailySummaries[dateKey];
  
  // Get assignments for selected date
  const dayAssignments = data?.assignments.filter(a => 
    isSameDateString(a.date, selectedDate)
  ) || [];

  // Group by gig to avoid duplicate cards, but show each contractor
  const gigsWithAssignments = dayAssignments.map(a => ({
    ...a,
    contractorName: data?.contractors.find(c => c.id === a.contractorId)?.name || 'Unknown'
  }));

  return (
    <div className="flex flex-col h-full">
      <WeekStrip 
        days={weekDays} 
        selectedDate={selectedDate} 
        onSelectDate={setSelectedDate}
        data={data}
      />
      
      <MobileDailyTotals 
        summary={dailySummary}
        expanded={totalsExpanded}
        onToggle={() => setTotalsExpanded(!totalsExpanded)}
      />
      
      {/* Gig Cards */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl font-bold text-hub-600">{format(selectedDate, 'd')}</span>
          <span className="text-sm text-gray-500">{format(selectedDate, 'EEE')}</span>
        </div>
        
        {gigsWithAssignments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No gigs scheduled for this day
          </div>
        ) : (
          gigsWithAssignments.map((assignment) => (
            <MobileGigCard
              key={assignment.id}
              assignment={assignment}
              onClick={() => navigateToGig(assignment.gigId)}
            />
          ))
        )}
      </div>
      
      {/* FAB for new gig */}
      <button
        onClick={() => navigateToNewGig(selectedDate)}
        className="fixed bottom-20 right-4 w-14 h-14 bg-hub-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-hub-700 transition-colors z-10"
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
}

// Add Unavailability Modal
function AddUnavailabilityModal({ isOpen, onClose, contractor, initialDate, onSave }) {
  const [startDate, setStartDate] = useState(format(initialDate, 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(initialDate, 'yyyy-MM-dd'));
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStartDate(format(initialDate, 'yyyy-MM-dd'));
      setEndDate(format(initialDate, 'yyyy-MM-dd'));
      setAllDay(true);
      setStartTime('09:00');
      setEndTime('17:00');
      setReason('');
      setError('');
    }
  }, [isOpen, initialDate]);

  // Generate time options (30 min intervals)
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const min = m.toString().padStart(2, '0');
      const value = `${hour}:${min}`;
      const label = new Date(`2000-01-01T${value}`).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      timeOptions.push({ value, label });
    }
  }

  const handleSave = async () => {
    setSaving(true);
    setError('');
    
    try {
      // Generate all dates in range
      // Parse dates at noon to avoid timezone day-shift issues
      const start = new Date(startDate + 'T12:00:00');
      const end = new Date(endDate + 'T12:00:00');
      const dates = eachDayOfInterval({ start, end });
      
      // Create availability entry for each date
      for (const date of dates) {
        await api.post('/availability', {
          contractorId: contractor.id,
          date: format(date, 'yyyy-MM-dd'),
          allDay,
          startTime: allDay ? null : startTime,
          endTime: allDay ? null : endTime,
          reason: reason || undefined
        });
      }
      
      onSave();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save unavailability');
    }
    setSaving(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Add Unavailability</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Contractor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contractor</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-hub-100 text-hub-600 flex items-center justify-center text-sm font-medium">
                {contractor?.name?.charAt(0)}
              </div>
              <span className="font-medium">{contractor?.name}</span>
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (e.target.value > endDate) {
                    setEndDate(e.target.value);
                  }
                }}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input"
              />
            </div>
          </div>

          {/* Quick Range Buttons */}
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setEndDate(startDate)}
              className={`text-xs px-2 py-1 rounded ${startDate === endDate ? 'bg-hub-100 text-hub-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Single Day
            </button>
            <button
              type="button"
              onClick={() => {
                const start = new Date(startDate + 'T12:00:00');
                const end = addDays(start, 6);
                setEndDate(format(end, 'yyyy-MM-dd'));
              }}
              className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              1 Week
            </button>
            <button
              type="button"
              onClick={() => {
                const start = new Date(startDate + 'T12:00:00');
                const end = addDays(start, 13);
                setEndDate(format(end, 'yyyy-MM-dd'));
              }}
              className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              2 Weeks
            </button>
          </div>

          {/* All Day Toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">All Day</label>
            <button
              type="button"
              onClick={() => setAllDay(!allDay)}
              className={`relative w-11 h-6 rounded-full transition-colors ${allDay ? 'bg-hub-600' : 'bg-gray-300'}`}
            >
              <span 
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${allDay ? 'translate-x-5' : 'translate-x-0'}`}
              />
            </button>
          </div>

          {/* Time Range (only if not all day) */}
          {!allDay && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <select
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="input"
                >
                  {timeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <select
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="input"
                >
                  {timeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Vacation, Other gig, Personal"
              className="input"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="btn-secondary flex-1"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="btn-primary flex-1"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Desktop View Component (existing grid)
function DesktopSchedulerView({ data, days, navigateToGig, getDailySummary, getAssignmentsForCell, getUnassignedForDay, getAvailabilityForCell, onDayClick, onEmptyCellClick, onAddUnavailability, onRemoveUnavailability, onQuickAdd, templates, onCancelGig, onDeleteGig }) {
  const [contextMenu, setContextMenu] = useState(null);
  const [cellMenu, setCellMenu] = useState(null);
  const [unavailMenu, setUnavailMenu] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [templateSubmenu, setTemplateSubmenu] = useState(false);

  // Smart menu positioning - keeps menu in viewport
  const getMenuPosition = (clickX, clickY, menuWidth = 200, menuHeight = 250) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 10;
    
    let x = clickX;
    let y = clickY;
    
    // If menu would go off right edge, flip to left
    if (clickX + menuWidth + padding > viewportWidth) {
      x = clickX - menuWidth;
    }
    
    // If menu would go off bottom, flip to top
    if (clickY + menuHeight + padding > viewportHeight) {
      y = clickY - menuHeight;
    }
    
    // Ensure not off left edge
    if (x < padding) x = padding;
    
    // Ensure not off top edge
    if (y < padding) y = padding;
    
    return { x, y };
  };

  const getStatusColor = (status, gigStatus) => {
    // CANCELLED gigs get red background with white text
    if (gigStatus === 'CANCELLED') {
      return 'border-red-600 bg-red-600 text-white';
    }
    // DRAFT gigs get yellow outline, no fill - override assignment status
    if (gigStatus === 'DRAFT') {
      return 'border-2 border-yellow-500 bg-white text-gray-700';
    }
    switch (status) {
      case 'PENDING': return 'border-yellow-300 text-yellow-800';
      case 'ACCEPTED': return 'border-green-300 text-green-800';
      case 'DECLINED': return 'border-red-300 text-red-800';
      case 'COMPLETED': return 'border-blue-300 text-blue-800';
      default: return 'border-gray-300 text-gray-800';
    }
  };

  const getStatusDot = (status, gigStatus) => {
    // CANCELLED gigs show white dot (on red background)
    if (gigStatus === 'CANCELLED') {
      return 'bg-white';
    }
    // DRAFT gigs show yellow dot
    if (gigStatus === 'DRAFT') {
      return 'bg-yellow-500';
    }
    switch (status) {
      case 'PENDING': return 'bg-yellow-400';
      case 'ACCEPTED': return 'bg-green-500';
      case 'DECLINED': return 'bg-red-500';
      case 'COMPLETED': return 'bg-blue-500';
      default: return 'bg-gray-400';
    }
  };

  const handleContextMenu = (e, assignment) => {
    e.preventDefault();
    e.stopPropagation();
    setCellMenu(null);
    setUnavailMenu(null);
    const pos = getMenuPosition(e.clientX, e.clientY, 160, 160);
    setContextMenu({
      x: pos.x,
      y: pos.y,
      gigId: assignment.gigId,
      gigStatus: assignment.gigStatus,
      assignmentId: assignment.id,
      assignmentStatus: assignment.status
    });
  };

  const handleCellMenuClick = (e, day, contractor) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu(null);
    setUnavailMenu(null);
    // Estimate height based on templates count
    const menuHeight = 120 + (templates?.length || 0) * 40;
    const pos = getMenuPosition(e.clientX, e.clientY, 200, menuHeight);
    setCellMenu({
      x: pos.x,
      y: pos.y,
      day,
      contractor
    });
  };

  const handleUnavailClick = (e, unavailable, contractor) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu(null);
    setCellMenu(null);
    const pos = getMenuPosition(e.clientX, e.clientY, 200, 100);
    setUnavailMenu({
      x: pos.x,
      y: pos.y,
      unavailable,
      contractor
    });
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
      setCellMenu(null);
      setUnavailMenu(null);
    };
    if (contextMenu || cellMenu || unavailMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu, cellMenu, unavailMenu]);

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[800px]">
          {/* Header Row - Daily Summaries */}
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 bg-gray-50 z-10 border-b border-r border-gray-200 p-2 text-left w-40">
                <span className="text-sm font-medium text-gray-600">Contractor</span>
              </th>
              {days.map((day) => {
                const summary = getDailySummary(day);
                return (
                  <th 
                    key={day.toISOString()} 
                    className={`border-b border-r border-gray-200 p-2 min-w-[140px] cursor-pointer hover:bg-hub-100 transition-colors ${
                      isToday(day) ? 'bg-hub-50' : ''
                    }`}
                    onClick={() => onDayClick(day)}
                  >
                    <div className="text-center">
                      <div className={`text-xs font-medium ${isToday(day) ? 'text-hub-600' : 'text-gray-500'}`}>
                        {format(day, 'EEE')}
                      </div>
                      <div className={`text-lg font-semibold ${isToday(day) ? 'text-hub-600' : 'text-gray-900'}`}>
                        {format(day, 'd')}
                      </div>
                      {summary && (
                        <div className="text-xs mt-1 space-y-0.5">
                          <div className="text-gray-500">
                            <span className="text-gray-400">Net:</span> {formatCurrency(summary.net)}
                          </div>
                          <div className={`font-medium margin-${getMarginStatus(summary.margin)}`}>
                            <span className="text-gray-400 font-normal">Margin:</span> {formatPercent(summary.margin)}
                          </div>
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          
          {/* Contractor Rows */}
          <tbody>
            {/* Unassigned Row - Always at top */}
            {data?.unassignedGigs?.length > 0 && (
              <tr className="bg-yellow-50">
                <td className="sticky left-0 bg-yellow-50 z-10 border-b border-r border-gray-200 p-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-yellow-200 flex items-center justify-center">
                      <span className="text-yellow-700 text-xs font-bold">?</span>
                    </div>
                    <span className="font-medium text-sm text-yellow-700">
                      Unassigned
                    </span>
                  </div>
                </td>
                {days.map((day) => {
                  const unassigned = getUnassignedForDay(day);
                  
                  return (
                    <td 
                      key={day.toISOString()} 
                      className={`border-b border-r border-gray-200 p-1 align-top ${
                        isToday(day) ? 'bg-yellow-100/50' : 'bg-yellow-50'
                      }`}
                    >
                      {unassigned.map((gig) => (
                        <div
                          key={gig.id}
                          onClick={() => navigateToGig(gig.gigId)}
                          className="text-xs p-1.5 mb-1 rounded border-2 border-dashed border-yellow-400 bg-yellow-100 cursor-pointer hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                            <span className="font-medium">
                              {new Date(`2000-01-01T${gig.startTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </span>
                          </div>
                          <div className="truncate font-medium text-yellow-800" title={`${gig.venue} - ${gig.location}`}>
                            {gig.location}
                          </div>
                          <div className="truncate text-yellow-600" title={gig.client}>
                            {gig.client}
                          </div>
                          <div className="text-yellow-700 font-medium mt-1">
                            Needs contractor!
                          </div>
                        </div>
                      ))}
                    </td>
                  );
                })}
              </tr>
            )}
            
            {data?.contractors.map((contractor) => (
              <tr key={contractor.id} className="hover:bg-gray-50">
                <td className="sticky left-0 bg-white z-10 border-b border-r border-gray-200 p-2">
                  <div className="flex items-center gap-2">
                    <Avatar name={contractor.name} photo={contractor.profilePhoto} size="sm" />
                    <span className="font-medium text-sm truncate max-w-[100px]" title={contractor.name}>
                      {contractor.name}
                    </span>
                  </div>
                </td>
                {days.map((day) => {
                  const assignments = getAssignmentsForCell(contractor.id, day);
                  const unavailable = getAvailabilityForCell(contractor.id, day);
                  const cellKey = `${contractor.id}-${format(day, 'yyyy-MM-dd')}`;
                  const isHovered = hoveredCell === cellKey;
                  const isEmpty = !unavailable && assignments.length === 0;
                  const hasGigs = assignments.length > 0;
                  
                  return (
                    <td 
                      key={day.toISOString()} 
                      className={`border-b border-r border-gray-200 p-1 align-top relative ${
                        isToday(day) ? 'bg-hub-50/50' : ''
                      } ${unavailable ? 'bg-red-50' : ''}`}
                      onMouseEnter={() => !unavailable && setHoveredCell(cellKey)}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      {/* Empty cell hover actions - centered */}
                      {isEmpty && isHovered && (
                        <div className="absolute inset-0 flex items-center justify-center gap-1 bg-gray-100/80">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEmptyCellClick(day, contractor.id);
                            }}
                            className="w-7 h-7 rounded-full bg-hub-600 text-white flex items-center justify-center hover:bg-hub-700 transition-colors shadow-sm"
                            title="Add Gig"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => handleCellMenuClick(e, day, contractor)}
                            className="w-7 h-7 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700 transition-colors shadow-sm"
                            title="More options"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {unavailable && (
                        <div 
                          onClick={(e) => handleUnavailClick(e, unavailable, contractor)}
                          className="text-xs text-red-600 text-center py-2 cursor-pointer hover:bg-red-100 rounded transition-colors"
                        >
                          <div className="font-medium">Unavailable</div>
                          {!unavailable.allDay && (
                            <div>
                              {new Date(`2000-01-01T${unavailable.startTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                              {' - '}
                              {new Date(`2000-01-01T${unavailable.endTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </div>
                          )}
                          {unavailable.reason && (
                            <div className="text-red-500 truncate" title={unavailable.reason}>{unavailable.reason}</div>
                          )}
                        </div>
                      )}
                      
                      {assignments.map((assignment) => {
                        const bgColor = assignment.clientColor || undefined;
                        const isDraft = assignment.gigStatus === 'DRAFT';
                        return (
                          <div
                            key={assignment.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToGig(assignment.gigId);
                            }}
                            onContextMenu={(e) => handleContextMenu(e, assignment)}
                            className={`text-xs p-1.5 mb-1 rounded border cursor-pointer hover:shadow-md transition-shadow relative group ${getStatusColor(assignment.status, assignment.gigStatus)}`}
                            style={isDraft ? {} : (bgColor ? { backgroundColor: bgColor } : undefined)}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <div className="flex items-center gap-1">
                                <span className={`w-2 h-2 rounded-full ${getStatusDot(assignment.status, assignment.gigStatus)}`}></span>
                                {isDraft && <span className="text-yellow-600 font-bold text-[10px]">DRAFT</span>}
                                {assignment.gigStatus === 'CANCELLED' && <span className="text-white font-bold text-[10px]">CANCELLED</span>}
                                <span className="font-medium">{new Date(`2000-01-01T${assignment.startTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                              </div>
                              <button
                                onClick={(e) => handleContextMenu(e, assignment)}
                                className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-black/10 rounded transition-opacity"
                              >
                                <MoreVertical className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="truncate font-medium" title={`${assignment.venue} - ${assignment.location}`}>
                              {assignment.location}
                            </div>
                            <div className="truncate text-gray-600" title={assignment.client}>
                              {assignment.client}
                            </div>
                            {assignment.role && (
                              <div className="text-gray-500">{assignment.role}</div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Add buttons for cells with existing gigs - bottom of cell */}
                      {hasGigs && isHovered && (
                        <div className="flex items-center justify-center gap-1 mt-1 py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEmptyCellClick(day, contractor.id);
                            }}
                            className="w-6 h-6 rounded-full bg-hub-600 text-white flex items-center justify-center hover:bg-hub-700 transition-colors shadow-sm"
                            title="Add Another Gig"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => handleCellMenuClick(e, day, contractor)}
                            className="w-6 h-6 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700 transition-colors shadow-sm"
                            title="More options"
                          >
                            <MoreHorizontal className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Gig Context Menu */}
      {contextMenu && (
        <div 
          className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[140px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            onClick={() => {
              navigateToGig(contextMenu.gigId);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            Edit Gig
          </button>
          {contextMenu.assignmentStatus === 'PENDING' && contextMenu.gigStatus !== 'CANCELLED' && (
            <button
              onClick={() => {
                handleForceAccept(contextMenu.assignmentId);
                setContextMenu(null);
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-green-600 flex items-center gap-2"
            >
              Force Accept
            </button>
          )}
          {contextMenu.gigStatus !== 'CANCELLED' && (
            <button
              onClick={() => {
                onCancelGig(contextMenu.gigId);
                setContextMenu(null);
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-orange-600 flex items-center gap-2"
            >
              Cancel Gig
            </button>
          )}
          <button
            onClick={() => {
              onDeleteGig(contextMenu.gigId);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center gap-2"
          >
            Delete Gig
          </button>
        </div>
      )}

      {/* Cell Action Menu (for empty cells) */}
      {cellMenu && (
        <div 
          className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[180px]"
          style={{ top: cellMenu.y, left: cellMenu.x }}
        >
          <button
            onClick={() => {
              onEmptyCellClick(cellMenu.day, cellMenu.contractor.id);
              setCellMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Gig
          </button>
          <button
            onClick={() => {
              onAddUnavailability(cellMenu.day, cellMenu.contractor);
              setCellMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <Calendar className="w-4 h-4" />
            Add Unavailability
          </button>
          
          {templates && templates.length > 0 && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase">Quick Add</div>
              {templates.map(template => (
                <button
                  key={template.id}
                  onClick={() => {
                    onQuickAdd(template.id, cellMenu.day, cellMenu.contractor.id);
                    setCellMenu(null);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-hub-50 flex items-center gap-2"
                >
                  <Zap className="w-4 h-4 text-hub-600" />
                  <span className="truncate">{template.name}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {/* Unavailability Menu */}
      {unavailMenu && (
        <div 
          className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[180px]"
          style={{ top: unavailMenu.y, left: unavailMenu.x }}
        >
          <div className="px-4 py-2 border-b border-gray-100">
            <div className="text-xs text-gray-500">Unavailable</div>
            <div className="font-medium text-sm">{unavailMenu.contractor?.name}</div>
          </div>
          <button
            onClick={() => {
              onRemoveUnavailability(unavailMenu.unavailable.id);
              setUnavailMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Remove Unavailability
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminScheduler() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  // Restore state from navigation (when coming back from gig detail)
  const restoredState = location.state;
  
  const [view, setView] = useState(restoredState?.view || 'week'); // 'day', 'week', 'month'
  const [previousView, setPreviousView] = useState(restoredState?.previousView || null); // Track where we came from
  const [currentDate, setCurrentDate] = useState(
    restoredState?.currentDate ? new Date(restoredState.currentDate) : new Date()
  );
  const [selectedDate, setSelectedDate] = useState(
    restoredState?.selectedDate ? new Date(restoredState.selectedDate) : new Date()
  ); // For mobile day selection
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  // Unavailability modal state
  const [unavailModal, setUnavailModal] = useState({ open: false, contractor: null, date: new Date() });
  
  // Templates for quick-add
  const [templates, setTemplates] = useState([]);
  
  // Bulk gig modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [hierarchy, setHierarchy] = useState([]);
  
  // Export modal
  const [showExportModal, setShowExportModal] = useState(false);

  // Clear location state after restoring (so refresh doesn't keep old state)
  useEffect(() => {
    if (restoredState) {
      window.history.replaceState({}, document.title);
    }
  }, []);

  // Navigate to gig with state preservation
  const navigateToGig = (gigId) => {
    navigate(`/admin/gigs/${gigId}`, {
      state: {
        returnTo: '/admin/scheduler',
        view,
        previousView,
        currentDate: currentDate.toISOString(),
        selectedDate: selectedDate.toISOString()
      }
    });
  };

  // Navigate to new gig form with state preservation
  const navigateToNewGig = (date, contractorId) => {
    let url = `/admin/gigs/new?date=${format(date || currentDate, 'yyyy-MM-dd')}`;
    if (contractorId) {
      url += `&contractorId=${contractorId}`;
    }
    navigate(url, {
      state: {
        returnTo: '/admin/scheduler',
        view,
        previousView,
        currentDate: currentDate.toISOString(),
        selectedDate: selectedDate.toISOString()
      }
    });
  };

  // Open unavailability modal
  const openUnavailabilityModal = (date, contractor) => {
    setUnavailModal({ open: true, contractor, date });
  };

  // Cancel gig from context menu
  const handleCancelGig = async (gigId) => {
    if (!confirm('Cancel this gig?')) return;
    try {
      await api.put(`/gigs/${gigId}/cancel`);
      // Reload data
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  // Delete gig from context menu
  const handleDeleteGig = async (gigId) => {
    if (!confirm('Permanently delete this gig? This cannot be undone.')) return;
    try {
      await api.delete(`/gigs/${gigId}`);
      // Reload data
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  // Force accept assignment from context menu
  const handleForceAccept = async (assignmentId) => {
    try {
      await api.post(`/assignments/${assignmentId}/accept`);
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  // Remove unavailability
  const handleRemoveUnavailability = async (availabilityId) => {
    if (!confirm('Remove this unavailability?')) return;
    try {
      await api.delete(`/availability/${availabilityId}`);
      // Reload data
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  // Quick-add gig from template
  const handleQuickAdd = async (templateId, date, contractorId) => {
    try {
      await api.post(`/templates/${templateId}/quick-add`, {
        date: format(date, 'yyyy-MM-dd'),
        contractorId
      });
      // Reload data to show new gig
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  // Navigate to day view from week/month (clicking on a day header)
  const goToDayView = (date) => {
    setPreviousView(view); // Remember where we came from
    setCurrentDate(date);
    setView('day');
  };

  // Go back to previous view (for back button behavior)
  const goBackFromDayView = () => {
    if (previousView) {
      setView(previousView);
      setPreviousView(null);
    } else {
      setView('week'); // Default fallback
    }
  };

  // Calculate date range based on view
  const getDateRange = () => {
    if (view === 'day') {
      return { start: currentDate, end: currentDate };
    } else if (view === 'week') {
      return { 
        start: startOfWeek(currentDate, { weekStartsOn: 1 }), // Monday
        end: endOfWeek(currentDate, { weekStartsOn: 1 }) 
      };
    } else {
      return { 
        start: startOfMonth(currentDate), 
        end: endOfMonth(currentDate) 
      };
    }
  };

  const { start, end } = getDateRange();
  const days = eachDayOfInterval({ start, end });
  
  // For mobile, always use week view for the strip
  const mobileWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const mobileWeekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: mobileWeekStart, end: mobileWeekEnd });

  // Load scheduler data function (extracted for reuse)
  const loadData = async () => {
    // Calculate date range
    let fetchStart, fetchEnd;
    if (isMobile) {
      fetchStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      fetchEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    } else if (view === 'day') {
      fetchStart = currentDate;
      fetchEnd = currentDate;
    } else if (view === 'week') {
      fetchStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      fetchEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    } else {
      fetchStart = startOfMonth(currentDate);
      fetchEnd = endOfMonth(currentDate);
    }
    
    setLoading(true);
    try {
      const [result, templatesResult, hierarchyResult] = await Promise.all([
        api.get(`/scheduler?startDate=${format(fetchStart, 'yyyy-MM-dd')}&endDate=${format(fetchEnd, 'yyyy-MM-dd')}`),
        api.get('/templates'),
        api.get('/locations/hierarchy')
      ]);
      setData(result);
      setTemplates(templatesResult);
      setHierarchy(hierarchyResult);
    } catch (err) {
      console.error('Failed to load scheduler data:', err);
    }
    setLoading(false);
  };

  // Load data on mount and when dependencies change
  useEffect(() => {
    loadData();
  }, [currentDate, view, isMobile]);

  // Navigation
  const navigate_prev = () => {
    if (isMobile || view === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else if (view === 'day') {
      setCurrentDate(addDays(currentDate, -1));
    } else {
      setCurrentDate(subMonths(currentDate, 1));
    }
  };

  const navigate_next = () => {
    if (isMobile || view === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else if (view === 'day') {
      setCurrentDate(addDays(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const goToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Get assignments for a specific contractor and day
  const getAssignmentsForCell = (contractorId, date) => {
    if (!data) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return data.assignments.filter(a => {
      const assignmentDateStr = typeof a.date === 'string' 
        ? a.date.split('T')[0] 
        : format(new Date(a.date), 'yyyy-MM-dd');
      return a.contractorId === contractorId && assignmentDateStr === dateStr;
    });
  };

  // Get unassigned gigs for a specific day
  const getUnassignedForDay = (date) => {
    if (!data?.unassignedGigs) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return data.unassignedGigs.filter(g => {
      const gigDateStr = typeof g.date === 'string'
        ? g.date.split('T')[0]
        : format(new Date(g.date), 'yyyy-MM-dd');
      return gigDateStr === dateStr;
    });
  };

  // Get availability for a specific contractor and day
  const getAvailabilityForCell = (contractorId, date) => {
    if (!data) return null;
    const dateStr = format(date, 'yyyy-MM-dd');
    return data.availability.find(a => {
      const availDateStr = typeof a.date === 'string'
        ? a.date.split('T')[0]
        : format(new Date(a.date), 'yyyy-MM-dd');
      return a.contractorId === contractorId && availDateStr === dateStr;
    });
  };

  // Get daily summary
  const getDailySummary = (date) => {
    if (!data) return null;
    const dateKey = format(date, 'yyyy-MM-dd');
    return data.dailySummaries[dateKey];
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="h-full flex flex-col -m-4">
        {/* Mobile Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button onClick={navigate_prev} className="p-2">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <h1 className="text-lg font-semibold">Schedule</h1>
            <p className="text-xs text-gray-500">
              {format(mobileWeekStart, 'MMM d')} - {format(mobileWeekEnd, 'MMM d, yyyy')}
            </p>
          </div>
          <button onClick={navigate_next} className="p-2">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        
        <MobileSchedulerView
          data={data}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          weekDays={weekDays}
          navigateToGig={navigateToGig}
          navigateToNewGig={navigateToNewGig}
        />
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="scheduler animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Scheduler</h1>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowExportModal(true)}
            className="btn-secondary"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button 
            onClick={() => setShowBulkModal(true)}
            className="btn-secondary"
          >
            <List className="w-4 h-4" />
            Add Multiple
          </button>
          <button 
            onClick={() => navigateToNewGig()}
            className="btn-primary"
          >
            <Plus className="w-4 h-4" />
            New Gig
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {/* Back button - only show in day view when we came from another view */}
          {view === 'day' && previousView && (
            <button 
              onClick={goBackFromDayView} 
              className="btn-ghost p-2 flex items-center gap-1 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to {previousView}</span>
            </button>
          )}
          <button onClick={navigate_prev} className="btn-ghost p-2">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={goToday} className="btn-secondary text-sm">Today</button>
          <button onClick={navigate_next} className="btn-ghost p-2">
            <ChevronRight className="w-5 h-5" />
          </button>
          <span className="font-semibold text-lg ml-2">
            {view === 'day' && format(currentDate, 'EEEE, MMMM d, yyyy')}
            {view === 'week' && `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`}
            {view === 'month' && format(currentDate, 'MMMM yyyy')}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['day', 'week', 'month'].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1 text-sm rounded-md capitalize ${
                  view === v ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Weekly/Monthly Totals Banner (Admin only) */}
      {data?.weeklyTotals && view !== 'day' && (
        <div className="card mb-4">
          <div className="card-body py-3">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="text-sm text-gray-500">
                {view === 'week' ? 'Weekly' : 'Monthly'} Totals
              </div>
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-sm text-gray-500">Billed:</span>
                  <span className="ml-2 font-semibold">{formatCurrency(data.weeklyTotals.billed)}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Net:</span>
                  <span className="ml-2 font-semibold">{formatCurrency(data.weeklyTotals.net)}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Payouts:</span>
                  <span className="ml-2 font-semibold">{formatCurrency(data.weeklyTotals.payout)}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Production Fee:</span>
                  <span className="ml-2 font-semibold">{formatCurrency(data.weeklyTotals.productionFee)}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Margin:</span>
                  <span className={`ml-2 font-bold margin-${getMarginStatus(data.weeklyTotals.margin)}`}>
                    {formatPercent(data.weeklyTotals.margin)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Gigs:</span>
                  <span className="ml-2 font-semibold">{data.weeklyTotals.gigCount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Totals Banner (day view only) */}
      {view === 'day' && (() => {
        const dailySummary = getDailySummary(currentDate);
        if (!dailySummary) return null;
        return (
          <div className="card mb-4">
            <div className="card-body py-3">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="text-sm text-gray-500">Daily Totals</div>
                <div className="flex items-center gap-6">
                  <div>
                    <span className="text-sm text-gray-500">Billed:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(dailySummary.billed)}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Net:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(dailySummary.net)}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Payouts:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(dailySummary.payout)}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Production Fee:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(dailySummary.productionFee)}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Margin:</span>
                    <span className={`ml-2 font-bold margin-${getMarginStatus(dailySummary.margin)}`}>
                      {formatPercent(dailySummary.margin)}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Gigs:</span>
                    <span className="ml-2 font-semibold">{dailySummary.gigCount}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Desktop Grid */}
      <DesktopSchedulerView
        data={data}
        days={days}
        navigateToGig={navigateToGig}
        getDailySummary={getDailySummary}
        getAssignmentsForCell={getAssignmentsForCell}
        getUnassignedForDay={getUnassignedForDay}
        getAvailabilityForCell={getAvailabilityForCell}
        onDayClick={goToDayView}
        onEmptyCellClick={(day, contractorId) => navigateToNewGig(day, contractorId)}
        onAddUnavailability={openUnavailabilityModal}
        onRemoveUnavailability={handleRemoveUnavailability}
        onQuickAdd={handleQuickAdd}
        templates={templates}
        onCancelGig={handleCancelGig}
        onDeleteGig={handleDeleteGig}
      />

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-sm text-gray-600 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded border-2 border-dashed border-yellow-400 bg-yellow-100"></span>
          <span>Unassigned</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded border-2 border-yellow-500 bg-white"></span>
          <span>Draft</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
          <span>Pending</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-green-500"></span>
          <span>Accepted</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-blue-500"></span>
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-red-500"></span>
          <span>Declined</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-red-600"></span>
          <span>Cancelled</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-red-100 border border-red-300"></span>
          <span>Unavailable</span>
        </div>
      </div>

      {/* Unavailability Modal */}
      <AddUnavailabilityModal
        isOpen={unavailModal.open}
        onClose={() => setUnavailModal({ open: false, contractor: null, date: new Date() })}
        contractor={unavailModal.contractor}
        initialDate={unavailModal.date}
        onSave={loadData}
      />

      {/* Bulk Gig Modal */}
      <BulkGigModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onCreated={(result) => {
          loadData();
          alert(`Created ${result.created} gig${result.created !== 1 ? 's' : ''}!`);
        }}
        hierarchy={hierarchy}
      />

      {/* Export Schedule Modal */}
      <ExportScheduleModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        hierarchy={hierarchy}
      />
    </div>
  );
}
